import React, { useMemo, useCallback, useState, useEffect } from "react";
import styles from "./QuizItem.module.scss";
import {
  Unit,
  useCancelFeedbackFormMutation,
} from "@api/graphql-codegen";
import OutlineIcon from "src/assets/icons/outline.svg?react";
import TicketIcon from "src/assets/icons/ticket.svg?react";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { EditText, CopyToClipboard } from '@src/utils/componentCasts';
import Button from "@src/components/button/Button";
import GridUnitsLayout from "../gridLayout/GridUnitsLayout";
import { MyQuizEnum, ExtendedQuiz } from "@src/types";
import Spinner from "@src/components/spinner/Spinner";
import { useToast, useCourseContent } from "@src/hooks";
import { createURL } from "@src/constants";
import { logger } from '../../../utils/logger';

interface Props {
  quiz: ExtendedQuiz;
  allUnits: Unit[];
  movedScroll: boolean;
  fetching?: boolean;
  handleOpenUnit: (quiz: Unit) => void;
  handleSaveChange: ({ name, value, previousValue }) => void;
  handleQuizPreview: (quiz: ExtendedQuiz) => void;
  handleGenerateQuiz: (quiz: ExtendedQuiz) => void;
  handleShowMyQuiz: (quiz: ExtendedQuiz) => void;
  showAddNewCourse: () => void;
  handleGenerateQuizLink: (quiz: ExtendedQuiz) => void;
  handleShowQrCode: (quiz: ExtendedQuiz) => void;
  quizRefresh: () => void;
}

const QuizItem = (props: Props) => {
  const {
    quiz,
    allUnits,
    movedScroll,
    fetching,
    handleSaveChange,
    handleOpenUnit,
    handleQuizPreview,
    handleGenerateQuiz,
    handleShowMyQuiz,
    showAddNewCourse,
    handleGenerateQuizLink,
    handleShowQrCode,
    quizRefresh,
  } = props;

  const [showMenu, setShowMenu] = useState(false);
  const { myQuizzes, setMyQuizzes } = useCourseContent();

  const { successToast } = useToast();

  const [cancelFeedbackForm, { loading: canceling }] =
    useCancelFeedbackFormMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      onCompleted(data) {
        if (data.cancelFeedbackForm) {
          quizRefresh()
          const updateQuizzes = myQuizzes.map((quiz: any, i: number) => {
            if (quiz.id === quiz?.id) {
              return {
                ...quiz,
                feedbackForm: undefined,
                expiryMinutes: 0,
                status: true,
              }
            } else {
              return quiz
            }
          })
          setMyQuizzes(updateQuizzes);
        }
      },
    });

  useEffect(() => {
    if (movedScroll) {
      setShowMenu(false);
    }
  }, [movedScroll]);

  const copyToClipboard = (e: any) => {
    setShowMenu(false);
    // setShowExitTicketActiveMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    successToast('Link Copied Successfully!');
  };

  const cancelExitTicket = (e: any) => {
    setShowMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }

    const formId = quiz.feedbackForm?.id;
    if (formId) {
      cancelFeedbackForm({
        variables: {
          input: { id: formId },
        },
      });
    }
  };

  const generateExitTicket = (e: any) => {
    setShowMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    if (handleGenerateQuizLink) {
      handleGenerateQuizLink(quiz);
    }
  };

  const showQuizQrCode = (e: any) => {
    setShowMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    if (handleShowQrCode) {
        handleShowQrCode(quiz);
    }
  };

  const handleExitTicketVisible = useCallback((visible: boolean) => {
    setShowMenu(visible);
  }, []);

  const quizLink = useMemo(() => {
    if(quiz.feedbackForm) {
      return createURL(quiz.feedbackForm.link ?? '')
    } else {
      return ''
    }
  }, [quiz.feedbackForm])

  const ticketActiveMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div
          className={styles.activeInfoMenu}
          onClick={(e) => e.stopPropagation()}
        >
          <OutlineIcon width={21} className={styles.warningIcon} />
          <div className={styles.activeInfo}>
            <span>
              {quiz.expiryMinutes && <strong>{Math.round(quiz.expiryMinutes / 60)}</strong>} Minutes Left
            </span>
            <span>
              <strong>{quiz.feedbackForm?.exitTicketCount}</strong> Responses
              Max
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>Cancel</div>,
      onClick: (e) => cancelExitTicket(e),
    },
    {
      key: "3",
      label: (
        <CopyToClipboard text={quizLink}>
          <div className={styles.dropDownMenuitem}>Copy Link</div>
        </CopyToClipboard>
      ),
      onClick: (e) => copyToClipboard(e),
    },
    {
      key: "4",
      label: <div className={styles.dropDownMenuitem}>Regenerate</div>,
      onClick: (e) => generateExitTicket(e),
    },
    {
      key: "5",
      label: <div className={styles.dropDownMenuitem}>Show QR Code</div>,
      onClick: (e) => showQuizQrCode(e),
    }
  ];

  return (
    <div id={quiz.id} key={quiz.id}>
      <div className={styles.quizViewHeader}>
        <div className={styles.quizViewTitle}>
          <div
            className={styles.subItem}
            style={{ background: `${quiz.darkColor}` }}
          >
            <div className={styles.quizTitle}>
              <EditText
                name={quiz.id}
                defaultValue={quiz?.name}
                inputClassName={styles.inputCourse}
                onSave={handleSaveChange}
              />
            </div>
          </div>
        </div>
        {allUnits.length > 0 && <div className={styles.quizActions}>
          <Button
            className={styles.previewQuizButton}
            size="small"
            color={"orange"}
            outline={true}
            label="Preview"
            onClick={() => handleQuizPreview(quiz)}
          />
          {quiz.expiryMinutes && quiz.expiryMinutes > 0 ? (
            <Dropdown
              menu={{ items: ticketActiveMenu }}
              trigger={["click"]}
              placement="bottomRight"
              onOpenChange={handleExitTicketVisible}
              open={showMenu}
            >
              <Button
                disabled={canceling || fetching}
                label="Link Active"
                size="small"
                className={styles.activeButton}
                onClick={(e) => e.stopPropagation()}
                leftIcon={
                  canceling || fetching ? (
                    <Spinner width={20} height={20} />
                  ) : (
                    <TicketIcon />
                  )
                }
              />
            </Dropdown>
          ) : (
            <Button
              className={styles.generateQuizButton}
              size="small"
              color="orange"
              label="Generate Link"
              onClick={() => handleGenerateQuiz(quiz)}
            />
          )}
        </div>}
      </div>
      {allUnits.length > 0 ? (
        <GridUnitsLayout
          source={0}
          units={allUnits}
          studyMode={MyQuizEnum.MyQuizzes}
          handleOpenUnit={handleOpenUnit}
        />
      ) : (
        <div className={styles.unitsDivStudyMode}>
          {quiz?.name ? (
            <div
              className={styles.emptyCourseView}
              onClick={() => handleShowMyQuiz(quiz)}
            >
              <div className={styles.insideDiv}>
                <h2>+</h2>
                <span>{"Add Unit"}</span>
              </div>
            </div>
          ) : (
            <div className={styles.blankCourseView} onClick={showAddNewCourse}>
              <div className={styles.insideDiv}>
                <h2>{"+"}</h2>
                <span>
                  {`Create Your First`}
                  <br /> Assessment!
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizItem;
